// ============================================================
//  ADMIN SERVICE — ADMIN DASHBOARD
//  Path: src/services/admin.service.ts
//
//  Business logic layer — calls admin.api.ts for HTTP
//
//  Review pass: no code changes — every function here maps to a real
//  admin.api.ts call, and all pages type-check against it. Three
//  comments were out of date and are corrected (document view-url
//  401s, tasker breakdown fields, geography null params).
// ============================================================

import {
  getDashboardStatsApi,
  listUsersApi,
  getUserByIdApi,
  toggleUserActiveApi,
  resetUserPasswordApi,
  deleteUserApi,
  dismissUserIssueApi,
  restoreUserIssueApi,
  listUserIssueDismissalsApi,
  listExpertsApi,
  listInsuranceReviewsApi,
  getInsurerStatusApi, listInsurerPoliciesApi, retryInsurerPolicyApi,
  getExpertByIdApi,
  getExpertSetupTimelineApi,
  listActiveTripsApi,
  dismissExpertIssueApi,
  restoreExpertIssueApi,
  listExpertIssueDismissalsApi,
  listCompaniesApi,
  approveCompanyApi,
  retryCompanyStripeAccountSwitchApi,
  revokeCompanyApprovalApi,
  deleteCompanyApi,
  listCompanyWorkerDirectoryApi,
  listPendingCompanyJoinRequestsApi,
  respondToCompanyJoinRequestApi,
  updateExpertStatusApi,
  reviewBackgroundCheckApi,
  listPendingBackgroundChecksApi,
  listPastDueSubscriptionExpertsApi,
  listExpertsPendingCertificationApi,
  listAllCategoriesAdminApi,
  getCategoryGroupOrderApi,
  updateCategoryApi,
  updateCategoryPickupFlagsApi,
  overrideExpertSubscriptionApi,
  listPendingVehicleVerificationsApi,
  reviewVehicleApi,
  listTasksApi,
  getTaskDetailApi,
  getTaskTimelineApi, getCustomerTimelineApi, getExpertTimelineApi, resolveDisputeApi, markTaskDoneForTaskerApi,
  updateTaskApi,
  deleteTaskApi,
  getTaskPdfApi,
  getFinancialReportApi,
  getFinancialReportPdfApi,
  getTaxLedgerReportApi,
  getTaskCoverageReportApi,
  getTaskCoverageCsvApi,
  getTaxProvinceRatesApi,
  getTaxLedgerCsvApi,
  getTaxLedgerPdfApi,
  getNegotiationForAdminApi,
  listReviewsForUserApi,
  listExpertDocumentsApi,
  getDocumentViewUrlApi,
  getExpertOnboardingStatusApi,
  verifyDocumentApi,
  certifyExpertApi,
  listTasksForCustomerApi,
  listTasksForExpertApi,
  getExpertEarningsApi,
  getCustomerPaymentHistoryApi,
  getPaymentsSummaryApi,
  listPaymentTransactionsApi,
  listTransactionsForTaskApi,
  retryFailedRefundApi,
  refundExpertSubscriptionApi,
  createAdminApi,
  listAdminsApi,
  updateAdminRoleApi,
  removeAdminApi,
  removeExpertCategoryApi,
  deleteExpertDocumentApi,
  removeExpertVehicleApi,
  getSessionNotesForAdminApi,
  upsertCategoryInsuranceFeeApi,
  deleteCategoryInsuranceFeeApi,
  listInsuranceDefaultsApi,
  setInsuranceDefaultApi,
  resetCategoryInsuranceFeeToDefaultApi,
  listCategoryLicenseRequirementsApi,
  createCategoryLicenseRequirementApi,
  updateCategoryLicenseRequirementApi,
  deleteCategoryLicenseRequirementApi,
  listExpertInsurancePoliciesApi,
  verifyExpertInsurancePolicyApi,
  updateExpertInsurancePolicyExpiryApi,
  deleteExpertInsurancePolicyApi,
  listAppVersionsApi,
  updateAppVersionApi,
  listPolicyVersionsApi,
  updatePolicyVersionApi,
  getPlatformFeeSettingsApi,
  updatePlatformFeeSettingsApi,
  getTaskerBreakdownApi,
  updateCategoryReachApi,
  getGeographyDataApi,
} from '../api/admin.api'
import {
  listAdvertisementsApi, createAdvertisementApi, updateAdvertisementApi,
  deleteAdvertisementApi, listActiveAdvertisementsApi,
  generateAdMediaUploadUrlApi, confirmAdMediaUploadApi,
  listScanFailedUploadTicketsApi, rescanUploadTicketApi,
  listSpotlightsApi, createSpotlightApi, updateSpotlightApi, deleteSpotlightApi,
} from '../api/admin.api'
import type { TaxReportParams, TaskCoverageParams } from '../api/admin.api'
import type { InsuranceType, LicenseVerificationLevel, DisputeResolution } from '../types'

// ── Dashboard ────────────────────────────────────────────────
export const getDashboardStats = () => getDashboardStatsApi()

// ── Users ────────────────────────────────────────────────────
export const listUsers = (params?: {
  page?: number; limit?: number; search?: string; role?: string; isActive?: string; issuesOnly?: string
  needsActionOnly?: string
}) => listUsersApi(params)

export const getUserById = (id: string) => getUserByIdApi(id)

export const toggleUserActive = (id: string, isActive: boolean) =>
  toggleUserActiveApi(id, isActive)

export const resetUserPassword = (id: string, newPassword: string) =>
  resetUserPasswordApi(id, newPassword)

// Permanently deletes a user and ALL their data — irreversible.
// super_admin only.
export const deleteUser = (id: string) => deleteUserApi(id)

// "Clear" / "Restore" this one issue on the Customer Detail page.
export const dismissUserIssue = (userId: string, issueCode: string) => dismissUserIssueApi(userId, issueCode)
export const restoreUserIssue = (userId: string, issueCode: string) => restoreUserIssueApi(userId, issueCode)
export const listUserIssueDismissals = (userId: string) => listUserIssueDismissalsApi(userId)

// ── Experts ──────────────────────────────────────────────────
export const listExperts = (params?: {
  page?: number; limit?: number; status?: string; issuesOnly?: string
  needsActionOnly?: string; taxNotDeclared?: string; setupPending?: string
}) => listExpertsApi(params)

export const getExpertById = (expertId: string) => getExpertByIdApi(expertId)
export const getExpertSetupTimeline = (expertId: string) => getExpertSetupTimelineApi(expertId)
export const listActiveTrips = (params?: { expertId?: string; limit?: number }) => listActiveTripsApi(params)

// ── Insurance review list ────────────────────────────────────
export const listInsuranceReviews = (params?: {
  page?: number; limit?: number; status?: 'pending' | 'accepted' | 'rejected' | 'all'; search?: string
}) => listInsuranceReviewsApi(params)

// "Clear" / "Restore" this one issue on the Expert Detail page.
export const dismissExpertIssue = (expertId: string, issueCode: string) => dismissExpertIssueApi(expertId, issueCode)
export const restoreExpertIssue = (expertId: string, issueCode: string) => restoreExpertIssueApi(expertId, issueCode)
export const listExpertIssueDismissals = (expertId: string) => listExpertIssueDismissalsApi(expertId)

export const listCompanies = (params?: {
  page?: number; limit?: number
}) => listCompaniesApi(params)

export const approveCompany = (companyId: string) => approveCompanyApi(companyId)
// Manual retry for the individual→business Stripe account switch
// approveCompany triggers automatically — see admin.api.ts's
// retryCompanyStripeAccountSwitchApi.
export const retryCompanyStripeAccountSwitch = (companyId: string) => retryCompanyStripeAccountSwitchApi(companyId)
export const revokeCompanyApproval = (companyId: string, reason: string) => revokeCompanyApprovalApi(companyId, reason)
export const deleteCompany = (companyId: string, reason: string) => deleteCompanyApi(companyId, reason)
export const listCompanyWorkerDirectory = (params?: {
  country?: string; province?: string; city?: string; page?: number; limit?: number
}) => listCompanyWorkerDirectoryApi(params)

// ── Worker-initiated roster join requests (admin override) ─────
// A worker whose only category has a 'company'-level licence
// requirement can request to join a company's roster themselves,
// instead of only waiting for the company to invite them — see
// companyWorker.service.js's requestToJoinCompany (backend) for the
// onboarding deadlock this exists to close. Normally the company
// accepts/declines it themselves; this is admin's override for when
// the company is slow to respond.
export const listPendingCompanyJoinRequests = () => listPendingCompanyJoinRequestsApi()
export const respondToCompanyJoinRequest = (companyWorkerId: string, accept: boolean) =>
  respondToCompanyJoinRequestApi(companyWorkerId, accept)

export const updateExpertStatus = (id: string, data: {
  status: string; reason?: string
}) => updateExpertStatusApi(id, data)

// ── Background Checks ────────────────────────────────────────
export const reviewBackgroundCheck = (id: string, data: {
  approved: boolean; reason?: string
}) => reviewBackgroundCheckApi(id, data)

export const listPendingBackgroundChecks = () => listPendingBackgroundChecksApi()
export const listPastDueSubscriptionExperts = () => listPastDueSubscriptionExpertsApi()
export const listExpertsPendingCertification = () => listExpertsPendingCertificationApi()

export const listAllCategoriesAdmin = () => listAllCategoriesAdminApi()
// Alias used by GeographyPage and TaskerBreakdownPage category filter dropdown
export const listAllCategories = listAllCategoriesAdmin

// Group display order — single source of truth (see CategoryGroup
// model in schema.prisma, GET /api/categories/groups in
// category.routes.js). Same endpoint expert-app and customer-app
// call; no admin-specific backend route needed since it only
// requires being logged in, not a specific role. Replaces the
// hardcoded CATEGORY_GROUP_ORDER array CategoriesPage.tsx used to
// keep separately, which had drifted out of sync with the real
// groups ('Moving & Delivery' matched nothing real).
export const getCategoryGroupOrder = () => getCategoryGroupOrderApi()

export const removeExpertCategory = (expertId: string, categoryId: string) =>
  removeExpertCategoryApi(expertId, categoryId)

export const updateCategory = (categoryId: string, data: {
  voiceCallPostHireEnabled?:     boolean
  videoCallPostHireEnabled?:     boolean
  voiceCallPreHireEnabled?:      boolean
  videoCallPreHireEnabled?:      boolean
  chatEnabled?:                  boolean
  callTimeLimitMinutes?:         number | null
  voiceCallPreHireLimitMinutes?:  number | null
  videoCallPreHireLimitMinutes?:  number | null
  voiceCallPostHireLimitMinutes?: number | null
  videoCallPostHireLimitMinutes?: number | null
  voiceCallPreHireMaxCalls?:     number | null
  videoCallPreHireMaxCalls?:     number | null
  voiceCallPostHireMaxCalls?:    number | null
  videoCallPostHireMaxCalls?:    number | null
  isActive?: boolean; isInHome?: boolean; requiresLicense?: boolean
  requiresBackgroundCheck?: boolean
  requiresVulnerableSectorCheck?: boolean
  allowsCustomerAddress?: boolean; allowsExpertLocation?: boolean; allowsVirtual?: boolean
  commissionExempt?: boolean; allowsZeroPrice?: boolean; commissionPercent?: number | null
  serviceFeeExempt?: boolean; serviceFeeType?: 'percent' | 'flat' | null; serviceFeeValue?: number | null
  serviceFeeCustomerPct?: number; serviceFeeTaskerPct?: number
  processingFeeExempt?: boolean; processingFeeType?: 'percent' | 'flat' | null; processingFeeValue?: number | null
  processingFeeCustomerPct?: number; processingFeeTaskerPct?: number
  allowsHourlyPricing?: boolean; allowsFlatPricing?: boolean; allowsFreeToConnect?: boolean
  vehicleRequirement?: 'any' | 'motorized' | 'car_van' | 'van_truck'
  insuranceFeeExempt?: boolean
  // Individual-expert liability insurance requirement — see
  // schema.prisma's Category model comment.
  requiresLiabilityInsurance?: boolean
  minLiabilityCoverageAmount?: number | null
  // 'manual' = fee rows; 'insurer' = live price from the connected insurer
  insurancePricing?: 'manual' | 'insurer'
}) => updateCategoryApi(categoryId, data)

// Pickup / two-location flag update — separate from updateCategory
// because these two flags have a dependency rule enforced on the
// backend (turning on requiresPickupCode auto-enables isTwoLocation,
// and vice versa when turning isTwoLocation off).
export const updateCategoryPickupFlags = (categoryId: string, data: {
  isTwoLocation?: boolean; requiresPickupCode?: boolean
}) => updateCategoryPickupFlagsApi(categoryId, data)

export const overrideExpertSubscription = (expertId: string, data: {
  subscriptionStatus: string; subscriptionRenewsAt?: string; reason?: string
}) => overrideExpertSubscriptionApi(expertId, data)

export const listPendingVehicleVerifications = () => listPendingVehicleVerificationsApi()

export const reviewVehicle = (vehicleId: string, data: {
  approved: boolean; rejectionReason?: string
}) => reviewVehicleApi(vehicleId, data)

// ── Tasks ────────────────────────────────────────────────────
export const listTasks = (params?: {
  page?: number; limit?: number; status?: string; paymentStatus?: string; categoryId?: string; search?: string
}) => listTasksApi(params)

export const getTaskDetail = (id: string) => getTaskDetailApi(id)

// ── Task timeline & activity tracks ───────────────────────────
export const getTaskTimeline     = (taskId: string)     => getTaskTimelineApi(taskId)
export const getCustomerTimeline = (customerId: string) => getCustomerTimelineApi(customerId)
export const getExpertTimeline   = (expertId: string)   => getExpertTimelineApi(expertId)

// ── Resolve a disputed task ───────────────────────────────────
export const resolveDispute = (taskId: string, data: { resolution: DisputeResolution; note?: string }) =>
  resolveDisputeApi(taskId, data)

// ── Mark as Done on the tasker's behalf ───────────────────────
export const markTaskDoneForTasker = (taskId: string, data: { note?: string }) =>
  markTaskDoneForTaskerApi(taskId, data)

export const updateTask = (id: string, data: {
  title?: string; description?: string; area?: string; address?: string; budget?: number
}) => updateTaskApi(id, data)

export const deleteTask = (id: string) => deleteTaskApi(id)

export const getTaskPdf = (id: string) => getTaskPdfApi(id)

export const getFinancialReport = (params: { scope: string; id?: string; period: string }) =>
  getFinancialReportApi(params)

export const getFinancialReportPdf = (params: { scope: string; id?: string; period: string }) =>
  getFinancialReportPdfApi(params)

export const getTaxLedgerReport = (params: TaxReportParams) => getTaxLedgerReportApi(params)
export const getTaskCoverageReport = (params: TaskCoverageParams) => getTaskCoverageReportApi(params)
export const getTaskCoverageCsv = (params: TaskCoverageParams) => getTaskCoverageCsvApi(params)
export const getTaxLedgerCsv = (params: TaxReportParams) => getTaxLedgerCsvApi(params)
export const getTaxLedgerPdf = (params: TaxReportParams) => getTaxLedgerPdfApi(params)
export const getTaxProvinceRates = () => getTaxProvinceRatesApi()

export const getNegotiationForAdmin = (applicationId: string) => getNegotiationForAdminApi(applicationId)
export const getSessionNotesForAdmin = (applicationId: string) => getSessionNotesForAdminApi(applicationId)

export const listReviewsForUser = (userId: string) => listReviewsForUserApi(userId)

export const listExpertDocuments = (expertId: string) => listExpertDocumentsApi(expertId)

// Architecture doc section 12 — call this right before opening a
// document preview, instead of reading fileUrl off the list response.
// See admin.api.ts's getDocumentViewUrlApi for what the backend does
// on each call (MFA check, ownership check, audit-log-first, then a
// short-lived signed link). It can return two different 401s, neither
// of which signs the admin out (config/api.ts):
//   - requiresReauth: true — the sign-in is older than 12 hours
//     (requireRecentAuth(720)); check with isReauthRequired().
//   - otherwise — this sign-in has no authenticator-app factor
//     (requireMfa).
// ExpertDetailPage.tsx and CompaniesPage.tsx show a message for each.
export const getDocumentViewUrl = (expertId: string, docId: string) =>
  getDocumentViewUrlApi(expertId, docId)

// The same resolver the expert app reads. Anything the dashboard needs
// to know about onboarding progress — what is outstanding, what was
// rejected and why, whether the expert can submit — is a field on this
// response. Deriving any of it from listExpertDocuments locally
// reintroduces the drift onboardingStatus.service.js was written to end.
export const getExpertOnboardingStatus = (expertId: string) => getExpertOnboardingStatusApi(expertId)
// insurancePolicy: only required when approving a document whose
// type represents insurance proof (liability_insurance,
// commercial_auto_insurance, insurance_proof) — see
// admin.service.js's verifyDocument (backend) for why. insuranceType
// is only needed for the generic 'insurance_proof' document type;
// the other two map to exactly one InsuranceType and the backend
// ignores this field for them.
export const verifyDocument = (
  expertId: string,
  docId: string,
  isVerified: boolean,
  reason?: string,
  insurancePolicy?: { insuranceType?: InsuranceType; provider: string; expiresAt: string | null }
) => verifyDocumentApi(expertId, docId, isVerified, reason, insurancePolicy)
export const deleteExpertDocument = (expertId: string, docId: string) => deleteExpertDocumentApi(expertId, docId)
export const removeExpertVehicle = (expertId: string, vehicleType: string) => removeExpertVehicleApi(expertId, vehicleType)
export const certifyExpert = (expertId: string, certified: boolean) => certifyExpertApi(expertId, certified)

export const listTasksForCustomer = (customerId: string) => listTasksForCustomerApi(customerId)
export const listTasksForExpert = (expertId: string) => listTasksForExpertApi(expertId)
export const getExpertEarnings = (expertId: string) => getExpertEarningsApi(expertId)
export const getCustomerPaymentHistory = (customerId: string) => getCustomerPaymentHistoryApi(customerId)

export const getPaymentsSummary = () => getPaymentsSummaryApi()

export const listPaymentTransactions = (params?: {
  page?: number; limit?: number; status?: string; type?: string; taskId?: string
}) => listPaymentTransactionsApi(params)

export const listTransactionsForTask = (taskId: string) => listTransactionsForTaskApi(taskId)

export const retryFailedRefund = (transactionId: string) => retryFailedRefundApi(transactionId)
export const refundExpertSubscription = (transactionId: string) => refundExpertSubscriptionApi(transactionId)

// ── Admin Management ─────────────────────────────────────────
export const createAdmin = (data: {
  email: string; password: string; firstName: string; lastName: string; role: string
}) => createAdminApi(data)

export const listAdmins = () => listAdminsApi()

export const updateAdminRole = (id: string, role: string) =>
  updateAdminRoleApi(id, role)

export const removeAdmin = (id: string) => removeAdminApi(id)

// ── CategoryInsuranceFee management (fee split model) ─────────
// Creates or updates a per-type insurance fee row for a category.
// insuranceType must be one of: liability | cargo | commercial_auto |
// professional_indemnity | general.
// customerPct + taskerPct must sum to 100 (enforced server-side).
// Default split: 0% customer / 100% tasker.
export const upsertCategoryInsuranceFee = (
  categoryId: string,
  insuranceType: InsuranceType,
  data: {
    feeType:          'percent' | 'flat'
    feeValue:         number
    customerPct?:     number
    taskerPct?:       number
    waivedIfVerified?: boolean
    coverageScope?:   string | null
    sortOrder?:       number
  }
) => upsertCategoryInsuranceFeeApi(categoryId, insuranceType, data)

export const deleteCategoryInsuranceFee = (categoryId: string, insuranceType: InsuranceType) =>
  deleteCategoryInsuranceFeeApi(categoryId, insuranceType)

// ── Platform-wide default insurance fees ──────────────────────
export const listInsuranceDefaults = () => listInsuranceDefaultsApi()

export const setInsuranceDefault = (
  insuranceType: InsuranceType,
  data: {
    feeType:           'percent' | 'flat'
    feeValue:          number
    customerPct?:      number
    taskerPct?:        number
    waivedIfVerified?: boolean
    coverageScope?:    string | null
  }
) => setInsuranceDefaultApi(insuranceType, data)

export const resetCategoryInsuranceFeeToDefault = (categoryId: string, insuranceType: InsuranceType) =>
  resetCategoryInsuranceFeeToDefaultApi(categoryId, insuranceType)

// ── CategoryLicenseRequirement management ─────────────────────
// A category's licence checklist. insuranceType (optional) is what
// the backend's verifyDocument reads to resolve which InsuranceType
// a document uploaded against this requirement proves — set here,
// once, at category-configuration time, instead of guessed later
// from the requirement's label at document-verification time.
export const listCategoryLicenseRequirements = (categoryId: string) =>
  listCategoryLicenseRequirementsApi(categoryId)

export const createCategoryLicenseRequirement = (
  categoryId: string,
  data: {
    docType: string; label: string; description?: string | null; insuranceType?: InsuranceType | null
    verificationLevel?: LicenseVerificationLevel
    isOptional?: boolean
  }
) => createCategoryLicenseRequirementApi(categoryId, data)

export const updateCategoryLicenseRequirement = (
  categoryId: string,
  requirementId: string,
  data: {
    docType?: string; label?: string; description?: string | null; insuranceType?: InsuranceType | null
    verificationLevel?: LicenseVerificationLevel
    isOptional?: boolean
  }
) => updateCategoryLicenseRequirementApi(categoryId, requirementId, data)

// Backend refuses (409) if any document is already uploaded against
// this requirement.
export const deleteCategoryLicenseRequirement = (categoryId: string, requirementId: string) =>
  deleteCategoryLicenseRequirementApi(categoryId, requirementId)

// ── Expert insurance policy admin management ──────────────────
// Lists all policies for one expert (verified and pending).
export const listExpertInsurancePolicies = (expertId: string) =>
  listExpertInsurancePoliciesApi(expertId)

// Verify or reject an expert's insurance policy.
// verified: true → policy is active for waiver checks on future hires.
// verified: false → policy is rejected; waiver will not apply.
export const verifyExpertInsurancePolicy = (
  policyId: string,
  data: { verified: boolean; note?: string }
) => verifyExpertInsurancePolicyApi(policyId, data)

// Update the expiry date of a policy (e.g. the expert renewed and
// only the date changed — avoids forcing a full re-upload).
export const updateExpertInsurancePolicyExpiry = (
  policyId: string,
  data: { expiresAt: string | null; note?: string }
) => updateExpertInsurancePolicyExpiryApi(policyId, data)

// Delete a stray policy row. Refused (409) when a past job used it.
export const deleteExpertInsurancePolicy = (policyId: string) => deleteExpertInsurancePolicyApi(policyId)
// ── App Version Control ───────────────────────────────────────
export const listAppVersions = () => listAppVersionsApi()
export const updateAppVersion = (
  appType: string,
  data: {
    minimumVersion?: string; latestVersion?: string
    // Whole numbers; null clears the check. Omit to leave as saved.
    minimumBuild?: number | null; latestBuild?: number | null
  }
) => updateAppVersionApi(appType, data)

// ── Policy Version Control ────────────────────────────────────
export const listPolicyVersions = () => listPolicyVersionsApi()
export const updatePolicyVersion = (
  policyType: string,
  data: { version?: string; effectiveDate?: string }
) => updatePolicyVersionApi(policyType, data)

// ── Platform Fee Settings ───────────────────────────────────────
export const getPlatformFeeSettings = () => getPlatformFeeSettingsApi()
export const updatePlatformFeeSettings = (data: {
  platformFeePercent?: number | null
  insuranceFeePercent?: number | null
  serviceFeeType?: 'percent' | 'flat' | null
  serviceFeeValue?: number | null
  processingFeePercent?: number | null
  processingFeeFixed?: number | null
  taxRatePercent?: number | null
}) => updatePlatformFeeSettingsApi(data)

// ── Tasker breakdown by location ──────────────────────────────
// Country → province → city. Every node has experts, onlineExperts,
// customers and activeCustomers (plus the older total/count fields);
// the response also has totalExperts / totalOnlineExperts /
// totalCustomers / totalActiveCustomers. Optional categoryId narrows
// the EXPERT numbers only — customer numbers come back null then,
// since customers have no category.
export const getTaskerBreakdown = (params?: { categoryId?: string }) =>
  getTaskerBreakdownApi(params)

// null / undefined = no filter on that level (axios leaves nulls out of
// the query string). To ask for people with a BLANK city/province, send
// '__unknown__' — GeographyPage's GEO_UNKNOWN.
export const getGeographyData = (params: {
  country?:  string | null
  province?: string | null
  city?:     string | null
  page?:     number
  limit?:    number
}) => getGeographyDataApi(params)

// ── Category reach + escalation update ───────────────────────
export const updateCategoryReach = (
  categoryId: string,
  data: {
    taskReach?:                  'local' | 'radius' | 'global'
    maxTaskRadiusKm?:            number | null
    allowCustomerToExpandReach?: boolean
    showAvailabilityCount?:      'none' | 'total' | 'online'
    escalationEnabled?:          boolean
    escalationStep1Minutes?:     number | null
    escalationStep2Minutes?:     number | null
    escalationStep3Minutes?:     number | null
  }
) => updateCategoryReachApi(categoryId, data)
// ── Advertisements ────────────────────────────────────────────

// Media upload — quarantine/scanner flow (see admin.api.ts's
// generateAdMediaUploadUrlApi/confirmAdMediaUploadApi and
// AdvertisementsPage.tsx's handleFileUpload for how these two are
// used together). Replaces the page's old direct-to-Firebase upload.
export const generateAdMediaUploadUrl = (data: { fileName: string; mimeType: string }) =>
  generateAdMediaUploadUrlApi(data)
export const confirmAdMediaUpload = (objectKey: string) =>
  confirmAdMediaUploadApi(objectKey)

export const listAdvertisements       = (params?: { isActive?: boolean }) => listAdvertisementsApi(params)
export const createAdvertisement      = (data: Parameters<typeof createAdvertisementApi>[0]) => createAdvertisementApi(data)
export const updateAdvertisement      = (id: string, data: Parameters<typeof updateAdvertisementApi>[1]) => updateAdvertisementApi(id, data)
export const deleteAdvertisement      = (id: string) => deleteAdvertisementApi(id)
export const listActiveAdvertisements = () => listActiveAdvertisementsApi()

// ── Upload tickets (quarantine/scanner pipeline) ───────────────
export const listScanFailedUploadTickets = (params?: { page?: number; limit?: number }) =>
  listScanFailedUploadTicketsApi(params)
export const rescanUploadTicket = (fileId: string) => rescanUploadTicketApi(fileId)

// ── Spotlights ────────────────────────────────────────────────
export const listSpotlights   = () => listSpotlightsApi()
export const createSpotlight  = (data: Parameters<typeof createSpotlightApi>[0]) => createSpotlightApi(data)
export const updateSpotlight  = (id: string, data: Parameters<typeof updateSpotlightApi>[1]) => updateSpotlightApi(id, data)
export const deleteSpotlight  = (id: string) => deleteSpotlightApi(id)
export const getInsurerStatus = () => getInsurerStatusApi()
export const listInsurerPolicies = (params?: { page?: number; limit?: number; status?: string; taskId?: string }) => listInsurerPoliciesApi(params)
export const retryInsurerPolicy = (id: string) => retryInsurerPolicyApi(id)